/**
 * ToolsetManager.js
 * Manages the toolset section and tools of the application
 */
export class ToolsetManager {
    constructor(app) {
        this.app = app;
        this.sceneManager = app.sceneManager;
        this.podManager = app.podManager;
        this.viewManager = app.viewManager;
        this.isActive = false;
        this.currentTool = null;
        this.currentHemisphere = null; // 'left' or 'right'

        // Left hemisphere tools
        this.leftTools = {
            spiral: {
                title: 'SPIRAλ',
                description: 'Coming Soon'
            }
        };

        // Right hemisphere tools
        this.rightTools = {
            noov: {
                title: 'NOØV_',
                description: 'Coming Soon'
            }
        };

        // Initialize background
        this.initBackground();
        this.createToolWindow();
    }

    async initBackground() {
        console.log('Initializing background...');
        const { NeuralBackground } = await import('../backgrounds/NeuralBackground.js');
        this.background = new NeuralBackground();
        this.background.init();
        console.log('Background initialized');
    }

    /**
     * Create the tool window
     */
    createToolWindow() {
        this.toolWindow = document.createElement('div');
        this.toolWindow.className = 'story-window tool-window';
        document.body.appendChild(this.toolWindow);
    }

    /**
     * Show the toolset section and transform the pod
     */
    show() {
        if (this.isActive) return;
        console.log('Showing toolset section');
        this.isActive = true;

        // Hide starfield
        if (this.sceneManager.starfield) {
            this.sceneManager.starfield.visible = false;
        }

        // Show spipa background
        if (this.background) {
            this.background.show();
        }

        // Transform the pod
        this.podManager.transformPodForToolsets();
    }

    /**
     * Hide the toolset section and reset the pod
     */
    hide() {
        if (!this.isActive) return;
        console.log('Hiding toolset section');
        this.isActive = false;

        // Show starfield again
        if (this.sceneManager.starfield) {
            this.sceneManager.starfield.visible = true;
        }

        // Hide spipa background
        if (this.background) {
            this.background.hide();
        }

        // Reset the pod
        this.podManager.resetPodAppearance();

        // Reset tool state
        this.currentTool = null;
        this.currentHemisphere = null;
    }

    /**
     * Show the tool window with content
     */
    showToolWindow(content, title) {
        // Clear previous content
        this.toolWindow.innerHTML = '';
        
        // Create header
        const header = document.createElement('div');
        header.className = 'story-header';
        const titleElement = document.createElement('h1');
        titleElement.textContent = title;
        header.appendChild(titleElement);

        // Create content container
        const contentContainer = document.createElement('div');
        contentContainer.className = 'story-content';
        contentContainer.innerHTML = content;

        // Add elements to window
        this.toolWindow.appendChild(header);
        this.toolWindow.appendChild(contentContainer);
        this.toolWindow.classList.add('visible');

        // Create toggle and add it to the title
        const toggle = this.viewManager.createToggleSwitch(this.toolWindow);
        toggle.addEventListener('change', (e) => {
            this.toggleWindow(e.target.checked);
        });
        titleElement.appendChild(toggle);

        // Show toggle and ensure content is visible
        this.viewManager.showToggle();
    }

    /**
     * Toggle the tool window visibility
     */
    toggleWindow(visible) {
        if (visible) {
            this.toolWindow.classList.add('visible');
        } else {
            this.toolWindow.classList.remove('visible');
            // Always go back to toolsets menu when toggle is off
            this.currentTool = null;
            this.app.uiManager.showMenu('toolsets');
        }
    }

    /**
     * Hide the tool window
     */
    hideToolWindow() {
        if (this.viewManager && this.viewManager.toggleInput) {
            this.viewManager.toggleInput.checked = false;
        }
        
        if (this.toolWindow) {
            this.toolWindow.classList.remove('visible');
        }
        
        if (this.viewManager) {
            this.viewManager.hideToggle();
        }

        this.currentTool = null;
    }

    /**
     * Handle left hemisphere selection
     */
    selectLeftHemisphere(options = {}) {
        console.log('Left hemisphere selected');
        this.currentHemisphere = 'left';
        
        // Update URL unless specified not to
        const { updateHistory = true } = options;
        if (updateHistory) {
            history.pushState({}, '', '/toolsets/left');
        }
        
        this.showLeftToolList();
    }

    /**
     * Handle right hemisphere selection
     */
    selectRightHemisphere(options = {}) {
        console.log('Right hemisphere selected');
        this.currentHemisphere = 'right';
        
        // Update URL unless specified not to
        const { updateHistory = true } = options;
        if (updateHistory) {
            history.pushState({}, '', '/toolsets/right');
        }
        
        this.showRightToolList();
    }

    /**
     * Show the left hemisphere tool list
     */
    showLeftToolList() {
        const content = `
            <div class="tool-list">
                ${Object.entries(this.leftTools).map(([id, tool]) => `
                    <div class="tool-item" data-tool-id="${id}">
                        <h2>${tool.title}</h2>
                        <p>${tool.description}</p>
                    </div>
                `).join('')}
            </div>
        `;

        this.showToolWindow(content, 'LH');

        // Add click handlers for tools
        const toolItems = this.toolWindow.querySelectorAll('.tool-item');
        toolItems.forEach(item => {
            item.addEventListener('click', () => {
                const toolId = item.getAttribute('data-tool-id');
                this.showTool(toolId);
            });
        });
    }

    /**
     * Show the right hemisphere tool list
     */
    showRightToolList() {
        const content = `
            <div class="tool-list">
                ${Object.entries(this.rightTools).map(([id, tool]) => `
                    <div class="tool-item" data-tool-id="${id}">
                        <h2>${tool.title}</h2>
                        <p>${tool.description}</p>
                    </div>
                `).join('')}
            </div>
        `;

        this.showToolWindow(content, 'RH');

        // Add click handlers for tools
        const toolItems = this.toolWindow.querySelectorAll('.tool-item');
        toolItems.forEach(item => {
            item.addEventListener('click', () => {
                const toolId = item.getAttribute('data-tool-id');
                this.showTool(toolId);
            });
        });
    }

    /**
     * Show a specific tool
     */
    showTool(toolId) {
        const tool = this.currentHemisphere === 'left' ? 
            this.leftTools[toolId] : 
            this.rightTools[toolId];
        if (!tool) return;

        this.currentTool = toolId;

        // Show tool content
        let content = '';
        if (toolId === 'noov') {
            content = `
                <div class="tool-header">
                    <button class="back-button">← Back</button>
                </div>
                <div class="coming-soon">
                    <h2>Coming Soon</h2>
                    <p>NOØV_ is currently under development.</p>
                </div>
            `;
        } else if (toolId === 'spiral') {
            content = `
                <div class="tool-header">
                    <button class="back-button">← Back</button>
                </div>
                <div class="coming-soon">
                    <h2>Coming Soon</h2>
                    <p>SPIRAλ is currently under development.</p>
                </div>
            `;
        }

        this.showToolWindow(content, tool.title);

        // Add back button handler
        const backButton = this.toolWindow.querySelector('.back-button');
        if (backButton) {
            backButton.addEventListener('click', () => {
                this.currentTool = null;
                // Show tool list based on current hemisphere
                if (this.currentHemisphere === 'left') {
                    this.showLeftToolList();
                } else {
                    this.showRightToolList();
                }
            });
        }

        if (toolId === 'noov') {
            this.initNoov();
        }
    }

    /**
     * Initialize NOOV_ tool
     */
    async initNoov() {
        const container = this.toolWindow.querySelector('#noov-container');
        if (!container) return;

        // Import NoovCircle and PrayerTimeCalculator dynamically
        const { NoovCircle } = await import('../components/noov/NoovCircle.js');
        const { PrayerTimeCalculator } = await import('../utils/prayer/PrayerTimeCalculator.js');

        const calculator = new PrayerTimeCalculator();
        await calculator.setLocation('Mecca', 'Saudi Arabia'); // Default location

        new NoovCircle(container, calculator);
    }
}
